/**
 * Forward-geocode a household area (city / state / ZIP) to a
 * `{ lat, lng }` pair using OpenStreetMap's Nominatim service. Web
 * equivalent of iOS `CLGeocoder().geocodeAddressString(...)` (used in
 * `Views/Controls/AreaPicker.swift:237`) and the Android-side
 * platform geocoder.
 *
 * Why this matters: weekend suggestions (`functions/src/weekend.ts`)
 * read `households.{id}.location_lat / location_lng` to score nearby
 * activities. iOS + Android already write both; web previously
 * dropped them, leaving lat/lng stale or null and breaking the
 * radius-based ranker.
 *
 * Nominatim is free, requires no API key, and follows OSM's
 * rate-limit (≤ 1 req/sec, identifying User-Agent recommended) — a
 * one-off geocode on form submit fits comfortably under that limit.
 * Network or parse failures resolve to `null`; callers should still
 * persist the city / state / ZIP fields and silently skip the
 * lat/lng write so the UX isn't blocked by a flaky third-party.
 */

export interface GeocodeArea {
  lat: number;
  lng: number;
}

export interface GeocodeInput {
  city?: string;
  state?: string;
  zip?: string;
}

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";

export async function geocodeArea(
  input: GeocodeInput,
): Promise<GeocodeArea | null> {
  const parts = [input.city?.trim(), input.state?.trim(), input.zip?.trim()]
    .filter((p): p is string => Boolean(p));
  if (parts.length === 0) return null;

  const params = new URLSearchParams({
    format: "json",
    limit: "1",
    addressdetails: "0",
    q: parts.join(", "),
  });

  try {
    const res = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
      headers: {
        // OSM policy asks for an identifying User-Agent — use the
        // app name. Browsers ignore custom UAs in fetch headers, but
        // the Referer header (auto-sent) plus the Accept header
        // satisfy the practical requirement.
        Accept: "application/json",
      },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Array<{ lat?: string; lon?: string }>;
    const first = data[0];
    if (!first?.lat || !first?.lon) return null;
    const lat = Number(first.lat);
    const lng = Number(first.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  } catch {
    return null;
  }
}
